                                                                         import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { Cbsa, LinkedinIndustry, NaicsIndustry, NaicsSector, OccupationBroadGroup, OccupationDetailedOccupation, OccupationMajorGroup, OccupationMinorGroup, OccupationOnetOccupation, OrganizationLite, ZipCode } from "../api/models";
import { PreFilterService } from "../api/services";
import { notNullFilter, uniqueFilter } from "../tools/array";
import { cacheDb, CacheType } from './cacheDb';
import { GlobalApp } from "../tools/global";


@Injectable({
  providedIn: 'root'
})
export class PreFilterValuesService {

  protected regionValues: PfkRegionState[] = [];
  protected industryValues: PfkLinkedinIndustry[] = [];
  protected occupationValues: PfkOccupationMajorGroup[] = [];

  protected organizationEvenDone: boolean = false;
  protected organizationOddDone: boolean = false;
  protected evenOrganizations: OrganizationLite[] = [];
  protected oddOrganizations: OrganizationLite[] = [];

  protected regionsLoaded: boolean = false;
  protected industriesLoaded: boolean = false;
  protected occupationsLoaded: boolean = false;
  protected titlesLoaded: boolean = false;
  protected organizationsLoaded: boolean = false;

  protected titlesTotal: number = 1;
  protected titlesLoadedCount: number = 0;

  protected organizationsTotal: number = 1;
  protected organizationsLoadedCount: number = 0;

  public allDataLoadedChange: Subject<boolean> = new Subject<boolean>();
  public cacheLoadStateChanged: Subject<CacheLoadState> = new Subject<CacheLoadState>();

  private isFullScreenMode: boolean = false;
  constructor(
    public app: GlobalApp,
    public preFilterService: PreFilterService
  ) {

    var t = this.app.isReportExport.subscribe(
      (isFullScreenMode) => {
        this.isFullScreenMode = isFullScreenMode;
      });
  }

  public allDataLoaded(): boolean {
    return true;
    //disabling for now until pre-filters with Fabric store is updated
    //return this.regionsLoaded && this.industriesLoaded && this.occupationsLoaded && this.titlesLoaded && this.organizationsLoaded;
  }

  public getCacheLoadState(): CacheLoadState {
    return {
      allLoaded: this.allDataLoaded(),

      organizationsLoaded: this.organizationsLoaded,
      industriesLoaded: this.industriesLoaded,
      occupationsLoaded: this.occupationsLoaded,
      regionsLoaded: this.regionsLoaded,
      titlesLoaded: this.titlesLoaded,

      titlesLoadedCount: this.titlesLoadedCount,
      titlesTotal: this.titlesTotal,

      organizationsLoadedCount: this.organizationsLoadedCount,
      organizationsTotal: this.organizationsTotal
    }
  }

  public async cacheValues(): Promise<boolean> {
    return new Promise<boolean>(async (resolve, error) => {

      this.loadOrganizations();
      this.loadIndustries();
      this.loadOccupations();
      this.loadRegions();
      this.loadTitles();

    });
  }

  private async cacheAvailable(key: string): Promise<boolean> {
    var cacheType = await cacheDb.cacheTypes.where({ id: key }).first();

    if (cacheType) {
      var expire_date = new Date(cacheType.lastUpdated);
      expire_date.setDate(expire_date.getDate() + 7);
      if (expire_date >= new Date(Date.now())) {
        return true;
      }
    }

    if (key === 'organizations') {
      await cacheDb.cacheOrganizations.clear();
    } else if (key === 'titles') {
      await cacheDb.cacheTitles.clear();
    } else {
      await cacheDb.cacheItems
        .where('typeId').equals(key)
        .delete();
    }
  }

  private async readCachePages(key: string): Promise<string[]> {
    return (await cacheDb.cacheItems
      .where('typeId')
      .equals(key)
      .sortBy('page'))
      .map(x => x.value);
  }

  private async setCacheAvailable(key: string) {
    var cacheType = await cacheDb.cacheTypes.where({ id: key }).first();

    if (cacheType) {
      await cacheDb.cacheTypes.update(cacheType.id, {
        lastUpdated: new Date(Date.now())
      });
    } else {
      await cacheDb.cacheTypes.add({
        id: key,
        lastUpdated: new Date(Date.now())
      });
    }
  }

  private async writeCachePage(key: string, page: number, data: string) {
    await cacheDb.cacheItems.add({
      id: key + "_page_" + page.toString(),
      page: page,
      typeId: key,
      value: data
    });
  }

  private loadOrganizations = async () => {

    if (!(await this.cacheAvailable('organizations'))) {

      this.organizationsTotal = await this.preFilterService.preFilterGetOrganizationsCount().toPromise();
      this.organizationsLoadedCount = 0;
      this.cacheLoadStateChanged.next(this.getCacheLoadState());

      this.loadOrganizationsOdd();
      this.loadOrganizationsEven();

      const checkIntervalId = setInterval(() => {
        if (this.organizationEvenDone && this.organizationOddDone) {
          clearInterval(checkIntervalId);
          this.completeOrganizations();
        }
      }, 5000);
    } else {
      this.organizationsLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
    }
  };

  private loadOrganizationsEven = async () => {
    var returnCount = 1;
    var currentPage = 2;

    while (returnCount > 0 && !this.isFullScreenMode) {
      var organizationRows = JSON.parse(await this.preFilterService.preFilterGetOrganizationPageJson(currentPage).toPromise());
      returnCount = organizationRows.length;
      if (returnCount > 0) {
        await cacheDb.cacheOrganizations.bulkAdd(organizationRows.map((x) => ({
          organizationId: x.id.toString(),
          organizationName: x.name
        })));
        this.organizationsLoadedCount += returnCount;
        this.cacheLoadStateChanged.next(this.getCacheLoadState());
        currentPage += 2;
      }
    }

    this.organizationEvenDone = true;
  }

  private loadOrganizationsOdd = async () => {
    var returnCount = 1;
    var currentPage = 1;

    while (returnCount > 0 && !this.isFullScreenMode) {
      var organizationRows = JSON.parse(await this.preFilterService.preFilterGetOrganizationPageJson(currentPage).toPromise());
      returnCount = organizationRows.length;
      if (returnCount > 0) {
        await cacheDb.cacheOrganizations.bulkAdd(organizationRows.map((x) => ({
          organizationId: x.id.toString(),
          organizationName: x.name
        })));
        this.organizationsLoadedCount += returnCount;
        this.cacheLoadStateChanged.next(this.getCacheLoadState());
        currentPage += 2;
      }
    }

    this.organizationOddDone = true;
  }

  private completeOrganizations = async () => {
    await this.setCacheAvailable('organizations');
    this.organizationsLoaded = true;
    this.allDataLoadedChange.next(this.allDataLoaded());
    this.cacheLoadStateChanged.next(this.getCacheLoadState());
  }

  private loadIndustries = async () => {

    if (!(await this.cacheAvailable('linkedinSubIndustries'))) {
      var returnCount = 1;
      var currentPage = 1;

      var linkedinIndustries: LinkedinIndustry[] = [];

      while (returnCount > 0 && !this.isFullScreenMode) {
        var industryRows = JSON.parse(await this.preFilterService.preFilterGetLinkedinIndustryPageJson(currentPage).toPromise());
        returnCount = industryRows.length;
        if (returnCount > 0) {
          linkedinIndustries.push(...industryRows);
          currentPage++;
        }
      }

      this.industryValues = [];

      var uniqueIndustries = linkedinIndustries.map(x => x.linkedinIndustry1.trim()).filter(uniqueFilter);
      uniqueIndustries.forEach((x) => {
        var uniqueNotNullSubIndustries: PfkLinkedinSubIndustry[] = linkedinIndustries
          .filter(y => y.linkedinIndustry1.trim() === x)
          .map(y => y.linkedinSubindustry.trim())
          .filter(uniqueFilter)
          .filter(notNullFilter)
          .map((y) => {
            return {
              subIndustry: y
            }
          });

        uniqueNotNullSubIndustries = this.sortByStringProperty(uniqueNotNullSubIndustries, x => x.subIndustry);

        this.industryValues.push({
          industry: x,
          subIndustries: uniqueNotNullSubIndustries
        });
      });

      this.industryValues = this.sortByStringProperty(this.industryValues, (a) => a.industry);
      await this.writeCachePage('linkedinSubIndustries', 1, JSON.stringify(this.industryValues));
      await this.setCacheAvailable('linkedinSubIndustries');
      this.industriesLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    } else {
      var pages = await this.readCachePages('linkedinSubIndustries');
      this.industryValues = JSON.parse(pages[0]);
      this.industriesLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    }
  }

  private loadOccupations = async () => {

    if (!(await this.cacheAvailable('occupations'))) {
      var returnCount = 1;
      var currentPage = 1;

      var allOccupationMajorGroups: OccupationMajorGroup[] = [];
      var allOccupationMinorGroups: OccupationMinorGroup[] = [];
      var allOccupationBroadGroups: OccupationBroadGroup[] = [];
      var allOccupationDetails: OccupationDetailedOccupation[] = [];
      var allOccupationOnets: OccupationOnetOccupation[] = [];

    while (returnCount > 0 && !this.isFullScreenMode) {
      var majorRows = JSON.parse(await this.preFilterService.preFilterGetOccupationMajorGroupPageJson(currentPage).toPromise());
      returnCount = majorRows.length;
      if (returnCount > 0) {
        allOccupationMajorGroups.push(...majorRows);
        currentPage++;
      }
    }
      while (returnCount > 0) {
        var majorRows = JSON.parse(await this.preFilterService.preFilterGetOccupationMajorGroupPageJson(currentPage).toPromise());
        returnCount = majorRows.length;
        if (returnCount > 0) {
          allOccupationMajorGroups.push(...majorRows);
          currentPage++;
        }
      }

    returnCount = 1;
    currentPage = 1;
    while (returnCount > 0 && !this.isFullScreenMode) {
      var minorRows = JSON.parse(await this.preFilterService.preFilterGetOccupationMinorGroupPageJson(currentPage).toPromise());
      returnCount = minorRows.length;
      if (returnCount > 0) {
        allOccupationMinorGroups.push(...minorRows);
        currentPage++;
      }
    }
      returnCount = 1;
      currentPage = 1;
      while (returnCount > 0) {
        var minorRows = JSON.parse(await this.preFilterService.preFilterGetOccupationMinorGroupPageJson(currentPage).toPromise());
        returnCount = minorRows.length;
        if (returnCount > 0) {
          allOccupationMinorGroups.push(...minorRows);
          currentPage++;
        }
      }

    returnCount = 1;
    currentPage = 1;
    while (returnCount > 0 && !this.isFullScreenMode) {
      var broadRows = JSON.parse(await this.preFilterService.preFilterGetOccupationBroadGroupPageJson(currentPage).toPromise());
      returnCount = broadRows.length;
      if (returnCount > 0) {
        allOccupationBroadGroups.push(...broadRows);
        currentPage++;
      }
    }
      returnCount = 1;
      currentPage = 1;
      while (returnCount > 0) {
        var broadRows = JSON.parse(await this.preFilterService.preFilterGetOccupationBroadGroupPageJson(currentPage).toPromise());
        returnCount = broadRows.length;
        if (returnCount > 0) {
          allOccupationBroadGroups.push(...broadRows);
          currentPage++;
        }
      }

    returnCount = 1;
    currentPage = 1;
    while (returnCount > 0 && !this.isFullScreenMode) {
      var detailRows = JSON.parse(await this.preFilterService.preFilterGetOccupationDetailedOccupationPageJson(currentPage).toPromise());
      returnCount = detailRows.length;
      if (returnCount > 0) {
        allOccupationDetails.push(...detailRows);
        currentPage++;
      }
    }
      returnCount = 1;
      currentPage = 1;
      while (returnCount > 0) {
        var detailRows = JSON.parse(await this.preFilterService.preFilterGetOccupationDetailedOccupationPageJson(currentPage).toPromise());
        returnCount = detailRows.length;
        if (returnCount > 0) {
          allOccupationDetails.push(...detailRows);
          currentPage++;
        }
      }

    returnCount = 1;
    currentPage = 1;
    while (returnCount > 0 && !this.isFullScreenMode) {
      var onetRows = JSON.parse(await this.preFilterService.preFilterGetOccupationOnetOccupationPageJson(currentPage).toPromise());
      returnCount = onetRows.length;
      if (returnCount > 0) {
        allOccupationOnets.push(...onetRows);
        currentPage++;
      }
    }
      returnCount = 1;
      currentPage = 1;
      while (returnCount > 0) {
        var onetRows = JSON.parse(await this.preFilterService.preFilterGetOccupationOnetOccupationPageJson(currentPage).toPromise());
        returnCount = onetRows.length;
        if (returnCount > 0) {
          allOccupationOnets.push(...onetRows);
          currentPage++;
        }
      }

      this.occupationValues = [];

      allOccupationMajorGroups.forEach((majorGroup) => {
        var vMajorGroup: PfkOccupationMajorGroup = {
          majorGroupId: majorGroup.id,
          majorGroup: majorGroup.description,
          minorGroups: []
        };

        var distinctMinorGroupIds = [...new Set(allOccupationOnets.filter((onet) => onet.majorGroupId === majorGroup.id).map((onet) => onet.minorGroupId))];
        distinctMinorGroupIds.forEach((minorGroupId) => {
          var minorGroup = allOccupationMinorGroups.find(x => x.id === minorGroupId);

          var vMinorGroup: PfkOccupationMinorGroup = {
            majorGroupId: majorGroup.id,
            minorGroupId: minorGroupId,
            minorGroup: minorGroup.description,
            broadGroups: []
          }

          var distinctBroadGroupIds = [...new Set(allOccupationOnets.filter((onet) => onet.minorGroupId === minorGroupId).map((onet) => onet.broadGroupId))];
          distinctBroadGroupIds.forEach((broadGroupId) => {
            var broadGroup = allOccupationBroadGroups.find(x => x.id === broadGroupId);

            var vBroadGroup: PfkOccupationBroadGroup = {
              minorGroupId: minorGroupId,
              broadGroupId: broadGroupId,
              broadGroup: broadGroup.description,
              occupationDetails: []
            }

            var distinctDetailIds = [...new Set(allOccupationOnets.filter((onet) => onet.broadGroupId === broadGroupId).map((onet) => onet.detailedOccupationId))];
            distinctDetailIds.forEach((detailId) => {
              var detail = allOccupationDetails.find(x => x.id === detailId);

              var vDetail: PfkOccupationDetails = {
                broadGroupId: broadGroupId,
                detailId: detailId,
                detail: detail.description,
                occupationOnets: []
              }

              var onets = allOccupationOnets.filter((onet) => onet.detailedOccupationId == detailId);

              onets.forEach((onet) => {
                vDetail.occupationOnets.push({
                  detailId: detailId,
                  onetSocCode: onet.socCode,
                  onetOccupation: onet.description
                })
              })

              vDetail.occupationOnets = this.sortByStringProperty(vDetail.occupationOnets, (a) => a.onetOccupation);
              vBroadGroup.occupationDetails.push(vDetail);

            });

            vBroadGroup.occupationDetails = this.sortByStringProperty(vBroadGroup.occupationDetails, (a) => a.detail);
            vMinorGroup.broadGroups.push(vBroadGroup);
          });

          vMinorGroup.broadGroups = this.sortByStringProperty(vMinorGroup.broadGroups, (a) => a.broadGroup);
          vMajorGroup.minorGroups.push(vMinorGroup);
        });

        vMajorGroup.minorGroups = this.sortByStringProperty(vMajorGroup.minorGroups, (a) => a.minorGroup);
        this.occupationValues.push(vMajorGroup);
      });

      this.occupationValues = this.sortByStringProperty(this.occupationValues, (a) => a.majorGroup);
      await this.writeCachePage('occupations', 1, JSON.stringify(this.occupationValues));
      await this.setCacheAvailable('occupations');
      this.occupationsLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    } else {
      var pages = await this.readCachePages('occupations');
      this.occupationValues = JSON.parse(pages[0]);
      this.occupationsLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    }
  }

  private loadRegions = async () => {

    if (!(await this.cacheAvailable('regions'))) {
      var returnCount = 1;
      var currentPage = 1;

      var allCbsa: Cbsa[] = [];
      var allZipCodes: ZipCode[] = [];

    while (returnCount > 0 && !this.isFullScreenMode) {
      var cbsaRows = JSON.parse(await this.preFilterService.preFilterGetCbsaPageJson(currentPage).toPromise());
      returnCount = cbsaRows.length;
      if (returnCount > 0) {
        allCbsa.push(...cbsaRows);
        currentPage++;
      }
    }
      while (returnCount > 0) {
        var cbsaRows = JSON.parse(await this.preFilterService.preFilterGetCbsaPageJson(currentPage).toPromise());
        returnCount = cbsaRows.length;
        if (returnCount > 0) {
          allCbsa.push(...cbsaRows);
          currentPage++;
        }
      }

    returnCount = 1;
    currentPage = 1;
    while (returnCount > 0 && !this.isFullScreenMode) {
      var zipRows = JSON.parse(await this.preFilterService.preFilterGetZipCodePageJson(currentPage).toPromise());
      returnCount = zipRows.length;
      if (returnCount > 0) {
        allZipCodes.push(...zipRows);
        currentPage++;
      }
    }
      returnCount = 1;
      currentPage = 1;
      while (returnCount > 0) {
        var zipRows = JSON.parse(await this.preFilterService.preFilterGetZipCodePageJson(currentPage).toPromise());
        returnCount = zipRows.length;
        if (returnCount > 0) {
          allZipCodes.push(...zipRows);
          currentPage++;
        }
      }

      this.regionValues = [];

      var distinctStateNames = [...new Set(allZipCodes.map((zipCode) => zipCode.stateName))];
      distinctStateNames.forEach((stateName) => {
        var allStateMetroAreaCbsas = allZipCodes.filter((zipCode) => zipCode.stateName === stateName).map((zipCode) => zipCode.cbsa);
        var stateMetroAreaCbsas = [...new Set(allStateMetroAreaCbsas.map((cbsa) => cbsa))];
        var firstUnique = allZipCodes.find((zipCode) => zipCode.stateName === stateName);
        var regionState: PfkRegionState = {
          state: firstUnique.stateAbbreviation,
          stateName: firstUnique.stateName,
          metroAreas: []
        };

        stateMetroAreaCbsas.forEach((cbsaCode) => {
          var cbsa = allCbsa.find((c) => c.code === cbsaCode);
          if (cbsa) {
            regionState.metroAreas.push({
              state: firstUnique.stateAbbreviation,
              metroArea: cbsa.name,
              cbsaCode: cbsa.code
            });
          }
        });

        regionState.metroAreas = this.sortByStringProperty(regionState.metroAreas, (a) => a.metroArea);

        this.regionValues.push(regionState);
      });

      this.regionValues = this.sortByStringProperty(this.regionValues, (a) => a.stateName);
      await this.writeCachePage('regions', 1, JSON.stringify(this.regionValues));
      await this.setCacheAvailable('regions');
      this.regionsLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    } else {
      var pages = await this.readCachePages('regions');
      this.regionValues = JSON.parse(pages[0]);
      this.regionsLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    }
  }

  private loadTitles = async () => {

    if (!(await this.cacheAvailable('titles'))) {
      var returnCount = 1;
      var currentPage = 1;
      var totalPages = 0;

      this.titlesTotal = await this.preFilterService.preFilterGetTitlesCount().toPromise();
      this.titlesLoadedCount = 0;
      this.cacheLoadStateChanged.next(this.getCacheLoadState());

      while (returnCount > 0 && !this.isFullScreenMode) {
        var titleRows = JSON.parse(await this.preFilterService.preFilterGetTitlePageJson(currentPage).toPromise());
        returnCount = titleRows.length;
        if (returnCount > 0) {
          totalPages++;
          await cacheDb.cacheTitles.bulkAdd(
            titleRows.map(x => ({
              titleId: x.id,
              title: x.title
            }))
          );
          this.titlesLoadedCount += returnCount;
          this.cacheLoadStateChanged.next(this.getCacheLoadState());
          currentPage++;
        }
      }

      await this.setCacheAvailable('titles');
      this.titlesLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    } else {
      this.titlesLoaded = true;
      this.allDataLoadedChange.next(this.allDataLoaded());
      this.cacheLoadStateChanged.next(this.getCacheLoadState());
    }
  }


  public getRegionValues(): PfkRegionState[] {
    return this.regionValues;
  };

  public getIndustryValues(): PfkLinkedinIndustry[] {
    //return this.industryValues;
    return [];
  }

  public getOccupationValues(): PfkOccupationMajorGroup[] {
    return this.occupationValues;
  }

  public async getTitleValues(searchTerms: string[], andInsteadOfOr: boolean): Promise<PfkTitle[]> {
    if (searchTerms == null || searchTerms.length === 0) {
      return await cacheDb.cacheTitles.limit(500).toArray();
    } else if (searchTerms != null && searchTerms.length === 1) {
      return await cacheDb.cacheTitles.where("titleWords").startsWithIgnoreCase(searchTerms[0]).sortBy('title');
    }

    if (andInsteadOfOr) {
      var otherMatches = searchTerms.splice(1);
      return await cacheDb.cacheTitles.where("titleWords").startsWithIgnoreCase(searchTerms[0])
        .and((x) => {
          var oneMatches = false;
          x.titleWords.forEach(nw => {
            if (!oneMatches) {
              otherMatches.forEach(om => {
                if (!oneMatches && nw.toLowerCase().startsWith(om.toLowerCase())) {
                  oneMatches = true;
                }
              })
            }
          })

          return oneMatches;
        }).sortBy("title");
    } else {
      return await cacheDb.cacheTitles.where("titleWords").startsWithAnyOfIgnoreCase(searchTerms).sortBy("title");
    }
  }

  public async getOrganizationValues(searchTerms: string[], andInsteadOfOr: boolean): Promise<PfkOrganization[]> {
    if (searchTerms == null || searchTerms.length === 0) {
      return await cacheDb.cacheOrganizations.limit(500).toArray();
    } else if (searchTerms != null && searchTerms.length === 1) {
      return await cacheDb.cacheOrganizations.where("nameWords").startsWithIgnoreCase(searchTerms[0]).sortBy('organizationName');
    }

    if (andInsteadOfOr) {
      var otherMatches = searchTerms.splice(1);
      return await cacheDb.cacheOrganizations.where("nameWords").startsWithIgnoreCase(searchTerms[0])
        .and((x) => {
          var oneMatches = false;
          x.nameWords.forEach(nw => {
            if (!oneMatches) {
              otherMatches.forEach(om => {
                if (!oneMatches && nw.toLowerCase().startsWith(om.toLowerCase())) {
                  oneMatches = true;
                }
              })
            }
          })

          return oneMatches;
        }).sortBy("organizationName");
    } else {
      return await cacheDb.cacheOrganizations.where("nameWords").startsWithAnyOfIgnoreCase(searchTerms).sortBy("organizationName");
    }
  }

  public async getTitleValueById(titleId: string): Promise<PfkTitle> {
    return await cacheDb.cacheTitles.get(titleId);
  }

  public async getOrganizationValueById(organizationId: string): Promise<PfkOrganization> {
    return await cacheDb.cacheOrganizations.get(organizationId);
  }

  private matchesTerms(value: string, terms: string[], andInstreadOfOr: boolean): boolean {

    var lValue = value.toLowerCase();
    var lTerms: string[] = [];
    terms.forEach(x => lTerms.push(x.toLowerCase()));

    if (andInstreadOfOr) {
      var anyFailed = false;
      lTerms.forEach(term => {
        if (!lValue.includes(term)) {
          anyFailed = true;
        }
      });

      return !anyFailed;
    } else {
      var anyFound = false;
      lTerms.forEach(term => {
        if (lValue.includes(term)) {
          anyFound = true;
        }
      });

      return anyFound;
    }
  }

  private sortByStringProperty<T>(srcArray: T[], getPropFunc: (object: T) => string): T[] {
    return srcArray.sort((a, b) => {
      var aVal = getPropFunc(a);
      var bVal = getPropFunc(b);
      if (aVal > bVal) {
        return 1;
      } else if (aVal < bVal) {
        return -1;
      }
      return 0;
    });
  }
}

export interface PfkOrganization {
  organizationId: string;
  organizationName: string;
  nameWords?: string[];
}

export interface PfkLinkedinSubIndustry {
  subIndustry?: string;
}

export interface PfkLinkedinIndustry {
  industry?: string;
  subIndustries?: Array<PfkLinkedinSubIndustry>;
}

export interface PfkOccupationBroadGroup {
  minorGroupId?: string;
  broadGroupId?: string;
  broadGroup?: string;
  occupationDetails?: Array<PfkOccupationDetails>;
}

export interface PfkOccupationDetails {
  broadGroupId?: string;
  detailId?: string;
  detail?: string;
  occupationOnets?: Array<PfkOccupationOnet>;
}

export interface PfkOccupationMajorGroup {
  majorGroupId?: string;
  majorGroup?: string;
  minorGroups?: Array<PfkOccupationMinorGroup>;
}

export interface PfkOccupationMinorGroup {
  majorGroupId?: string;
  minorGroupId?: string;
  minorGroup?: string;
  broadGroups?: Array<PfkOccupationBroadGroup>;
}

export interface PfkOccupationOnet {
  detailId?: string;
  onetSocCode?: string;
  onetOccupation?: string;
}

export interface PfkRegionMetroArea {
  state?: string;
  metroArea?: string;
  cbsaCode?: string;
}

export interface PfkRegionState {
  state?: string;
  stateName?: string;
  metroAreas?: Array<PfkRegionMetroArea>;
}

export interface PfkTitle {
  titleId: number;
  title?: string;
  titleWords?: string[];
}

export interface CacheLoadState {
  allLoaded: boolean;

  organizationsLoaded: boolean;
  industriesLoaded: boolean;
  occupationsLoaded: boolean;
  regionsLoaded: boolean;
  titlesLoaded: boolean;

  titlesLoadedCount: number;
  titlesTotal: number;

  organizationsLoadedCount: number;
  organizationsTotal: number;
}
